// /src/pages/private/settings/Handles.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { useAuth } from '../../../authContext';
import { Modal } from 'react-bootstrap';
import cert from '../../../medias/shield_green.png';
import uncert from '../../../medias/shield_grey.png';
import copyIcon from '../../../medias/copy.svg';
import copiedIcon from '../../../medias/copied.svg';

function ErrorMessage({ message }) {
  if (message) return <p className="message-error">{message}</p>;
  return <div className="error-spacer"></div>;
}

function CountdownTimer({ initialMinutes = 20 }) {
  const [time, setTime] = useState(initialMinutes * 60);

  useEffect(() => {
    // English comment: countdown tick (1s)
    const timer = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(time / 60);
  const seconds = time % 60;

  return (
    <div>
      {minutes} minutes, {seconds} seconds
    </div>
  );
}

function HandlesTable() {
  const { t } = useTranslation();
  const { getUserInfo, handles } = useAuth();

  const [handleToken, setHandleToken] = useState('');
  const [data, setData] = useState([]);
  const [membershipData, setMembershipData] = useState([]);
  const [selectedHandle, setSelectedHandle] = useState(null);
  const [currentHandle, setCurrentHandle] = useState(null);
  const [showHandleModal, setShowHandleModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' ou 'edit'
  const [handle, setHandle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [handleError, setHandleError] = useState('');

  useEffect(() => {
    console.groupCollapsed("🔍 HandlesTable data update");
    console.log("Raw handles from useAuth():", handles);
    console.log("MembershipData from API:", membershipData);

    const updateData = (handlesLocal, membershipDataLocal) => {
      return handlesLocal.map(handleObj => {
        const membershipInfo = membershipDataLocal.find(m => m.handle === handleObj.handle) || {};

        const isRsiFound = !!(
          handleObj.found ||
          handleObj.confirmed ||
          handleObj.active
        );

        console.log(`Handle: ${handleObj.handle}`, {
          handleFound: handleObj.handleFound,
          handleConfirmed: handleObj.handleConfirmed,
          handleActive: handleObj.handleActive,
          computed_isRsiFound: isRsiFound
        });

        return {
          ...handleObj,
          isRsiFound,
          certified:
            handleObj.certification === 'Certified' ||
            membershipInfo.certified === true,
          membership:
            handleObj.membership ||
            membershipInfo.membership ||
            'none',
          hasAffiliation:
            handleObj.hasAffiliation ||
            membershipInfo.hasAffiliation ||
            false,
          visibility:
            handleObj.visibility ||
            membershipInfo.visibility ||
            null,
        };
      });
    };

    const updated = updateData(handles || [], membershipData || []);
    console.log("Computed data for table:", updated);
    console.groupEnd();

    setData(updated);
  }, [handles, membershipData]);

  useEffect(() => {
    const fetchMembershipData = async () => {
      try {
        const response = await axios.post('/api/user/membership', {}, { withCredentials: true });
        setMembershipData(response.data);
      } catch (error) {
        console.error('Error fetching membership data:', error);
      }
    };

    fetchMembershipData();
  }, []);

  const columns = React.useMemo(
    () => [
      { header: 'Handle', accessorKey: 'handle' },
      { header: 'Moniker', accessorKey: 'moniker' },
      {
        header: 'RSI',
        accessorKey: 'isRsiFound',
        cell: info => (
          <div className="status-cell">
            <div className={`status-indicator ${info.getValue() ? 'active' : 'inactive'}`}></div>
          </div>
        ),
      },
      {
        header: 'Membership',
        accessorKey: 'membership',
        cell: info => {
          const membership = info.row.original.membership;
          const hasAffiliation = info.row.original.hasAffiliation;

          let label = 'None';
          let cls = 'membership-none';

          if (membership === 'main' && !hasAffiliation) {
            label = 'Exclusive';
            cls = 'membership-exclusive';
          } else if (membership === 'main' && hasAffiliation) {
            label = 'Main';
            cls = 'membership-main';
          } else if (membership === 'affiliate') {
            label = 'Affiliated';
            cls = 'membership-affiliate';
          }

          return <span className={`membership-label ${cls}`}>{label}</span>;
        }
      },
      {
        id: 'master',
        header: 'Master',
        accessorKey: 'isMaster',
        cell: info => (
          <div className="status-cell">
            {info.getValue() ? '★' : ''}
          </div>
        )
      },
      {
        header: 'Certified',
        accessorKey: 'certified',
        cell: info => (
          <div className="status-cell">
            <img
              src={info.getValue() ? cert : uncert}
              alt={info.getValue() ? "Certified" : "Uncertified"}
            />
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (handleObj) => {
    setSelectedHandle(handleObj);
    setModalMode('edit');
    setShowHandleModal(true);
    setCurrentHandle(handleObj.handle);
    setHandle(handleObj.handle);
    setHandleToken('');
  };

  const closeModal = () => {
    setShowHandleModal(false);
    setHandle('');
    setHandleToken('');
  };

  const handleInputChange = (e) => {
    setHandle(e.target.value);
    setHandleToken('');
  };

  const submitHandle = () => {
    setIsSubmitting(true);

    axios.post('/api/handle/submit', { handle: handle }, { withCredentials: true })
      .then(response => {
        setIsSubmitting(false);
        setHandleToken(response.data.tempToken);
      })
      .catch(error => {
        console.error("Error submitting the handle: ", error);
        setIsSubmitting(false);
      });

    setHandleToken('receivedToken');
  };

  const updateHandle = () => {
    setIsSubmitting(true);

    axios.post('/api/handle/update', { oldHandle: currentHandle, newHandle: handle }, { withCredentials: true })
      .then(response => {
        setIsSubmitting(false);
        setHandleToken(response.data.tempToken);
      })
      .catch(error => {
        console.error("Error updating the handle: ", error);
        setIsSubmitting(false);
      });
  };

  const confirmHandle = () => {
    setIsSubmitting(true);

    axios.post('/api/handle/confirm', { tempToken: handleToken }, { withCredentials: true })
      .then(response => {
        setIsSubmitting(false);

        if (response.data.message === 'handleConfirmationSucceed') {
          getUserInfo();
          closeModal();
        }
      })
      .catch(error => {
        console.error("Error confirming the handle: ", error);
        setIsSubmitting(false);
      });
  };

  const openHandleModal = (mode, handleValue = null) => {
    setModalMode(mode);
    setCurrentHandle(handleValue);
    setHandle(mode === 'edit' && handleValue ? handleValue : '');
    setHandleToken('');
    setShowHandleModal(true);
  };

  const copyToClipboard = () => {
    if (navigator.clipboard && handleToken) {
      navigator.clipboard.writeText(handleToken)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 5000);
        })
        .catch(err => {
          console.error('Could not copy text: ', err);
        });
    }
  };

  return (
    <div>
      <div className="table-base handles">
        <table>
          <colgroup>
            <col className="col-handle" />
            <col className="col-moniker" />
            <col className="col-rsi" />
            <col className="col-membership" />
            <col className="col-master" /> 
            <col className="col-certified" />
          </colgroup>
          <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th key={header.id} className={`header-${header.column.id}`}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        </table>

        <div className="table-body-wrapper">
          <table>
            <colgroup>
              <col className="col-handle" />
              <col className="col-moniker" />
              <col className="col-rsi" />
              <col className="col-membership" />
              <col className="col-master" /> 
              <col className="col-certified" />
            </colgroup>
            <tbody>
              {table.getRowModel().rows.map(row => {
                const isSel = selectedHandle?.handle === row.original.handle;
                return (
                  <tr
                    key={row.id}
                    className={isSel ? 'selected-row' : ''}
                    onClick={() => handleRowClick(row.original)}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className={`cell-${cell.column.id}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <button
        className="btn-style1 new-handle"
        onClick={() => { openHandleModal('add'); setSelectedHandle(null); }}
      >
        {t('Add a New Handle')}
      </button>

      <Modal show={showHandleModal} onHide={closeModal} className="create-handle">
        <Modal.Body className="create-handle">
          <div className="create-handle">
            <div className="create-handle header">
              <h3 className="handle-h3">{modalMode === 'add' ? t('NewHandleTitle') : t('EditHandleTitle')}</h3>
            </div>
            <div className="create-handle content">
              <div className="create-handle column">
                {modalMode === 'edit' && (
                  <div className="input-container input-handle">
                    <input
                      type="text"
                      name="currentHandle"
                      value={currentHandle || ''}
                      className="handle-lockfield"
                      readOnly
                    />
                  </div>
                )}
                <div className="input-container input-handle">
                  <input
                    type="text"
                    name="handle"
                    placeholder={'RSI_Handle_Name'}
                    value={handle}
                    className="handle-field"
                    onChange={handleInputChange}
                    autoComplete=""
                  />
                </div>

                <div className='new-handle-token input-handle'>
                  <span className='new-handle-token'> </span>
                  <button
                    className='btn-style1 handle-generate-token'
                    onClick={modalMode === 'add' ? submitHandle : updateHandle}
                    disabled={!handle}
                  >
                    {t('GenerateToken')}
                  </button>
                  <div className="input-icon-wrapper">
                    <input
                      type="text"
                      readOnly
                      value={handleToken}
                      className="readonly-input"
                    />
                    <img
                      src={isCopied ? copiedIcon : copyIcon}
                      alt="Copy icon"
                      className={`copy-icon ${handleToken ? '' : 'icon-disabled'}`}
                      onClick={copyToClipboard}
                    />
                  </div>
                </div>

                {handleToken && <CountdownTimer initialMinutes={20} />}

                <button
                  className="btn-style1 btn-style-handle"
                  onClick={confirmHandle}
                  disabled={isSubmitting || !handleToken}
                >
                  {isSubmitting ? t('Submitting...') : t('ConfirmHandle')}
                </button>

                <ErrorMessage message={handleError} />
              </div>

              <div className="create-handle column bullet-points">
                <ol>
                  <li>Paste here your <strong>RSI HANDLE</strong> from your <strong>RSI character page</strong></li>
                  <li>Clic on the <strong>GENERATE A TOKEN</strong> button</li>
                  <li>
                    Past this token at the end of the <strong>SHORT BIO</strong> field in your{' '}
                    <a href="https://robertsspaceindustries.com/account/profile" target="_blank" rel="noopener noreferrer"> RSI profile page</a>
                  </li>
                  <li>Clic on the <strong>CONFIRM MY HANDLE</strong> button</li>
                </ol>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default HandlesTable;
